'use client'

import TransactionAPI from '@/api/resources/Transaction.api'
import Dropdown from '@/components/commonComponent/Dropwdown'
import TransactionDetailsModal from '@/components/commonComponent/TransactionDetailsModal'
import { StatusLabel } from '@/components/datatable/action-button'
import { TablePagination } from '@/components/datatable/pagination'
import RowPerPage from '@/components/datatable/rowperpage'
import { Button } from '@/components/ui/button'
import InputField from '@/components/ui/InputField.tsx'
import { InlineLoader } from '@/components/ui/loader'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
} from '@/components/ui/table'
import useDocumentTitle from '@/hooks/use-document-title'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { getPaginationInfo } from '@/store/selectors/paginationSelector'
import { setFilterRecords, setPageIndex, setSearchParam, setSorting, setTotalRecords, triggerRefresh } from '@/store/slices/paginationSlice'
import { setLoading, setTransactionData } from '@/store/slices/transactionSlice'
import { RootState } from '@/store/store'
import { errorToast } from '@/utils/alerts'
import { ICON_SIZE_1 } from '@/utils/constant'
import { debounceSearch } from '@/utils/debounce'
import {
    ColumnDef,
    flexRender,
    getCoreRowModel,
    useReactTable
} from '@tanstack/react-table'
import { AxiosError } from 'axios'
import {
    ArrowUpDown
} from 'lucide-react'
import * as React from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { z } from 'zod'

// eslint-disable-next-line react-refresh/only-export-components
export const schema = z.object({
    _id: z.string(),
    amount: z.number(),
    status: z.string(),
    createdAt: z.string(),
    'plan.title': z.string(),
    user: z.object({ _id: z.string(), name: z.string(), email: z.string() }),
    transactions: z.string()
})

const columns: ColumnDef<z.infer<typeof schema>>[] = [
    {
        id: 'amount',
        accessorFn: (row) => row.amount,
        header: 'Amount',
        cell: ({ getValue }) =>
            new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            }).format(getValue<number>()),
        enableHiding: false,
        enableColumnFilter: false
    },
    {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => {
            const status = getValue<string>()

            return <StatusLabel label={status === 'SUCCESS' ? 'SUCCESS' : 'FAILED'} variant={status === 'SUCCESS' ? 'success' : 'destructive'} />
        },
        enableHiding: false,
        enableColumnFilter: false
    },
    {
        accessorKey: 'plan.title',
        header: 'Plan',
        enableColumnFilter: false

    },
    {
        accessorKey: 'user',
        header: ({ column }) => (
            <Button
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
                User
                <ArrowUpDown size={ICON_SIZE_1} />
            </Button>
        ),
        enableColumnFilter: false,
        cell: ({ row }) => (
            <div className='flex justify-start items-center gap-2'>
                <img src="/images/profile.png" alt="user" className="w-9 h-9 rounded-full flex-shrink-0 object-cover" />
                <div className="ml-2 h-fit flex flex-col justify-center w-fit">
                    <div className="flex items-center">
                        <Link to={`/user/${row?.original?.user?._id}`}>
                            <p className={'text-black w-fit overflow-hidden text-ellipsis underline'} title={row?.original?.user?.name || 'NA'}>
                                {row?.original?.user?.name || 'NA'}
                            </p></Link>
                    </div>
                    {row?.original?.user?.email && (
                        <p className="text-sm text-gray-700 max-w-44 w-fit overflow-hidden text-ellipsis" title={row?.original?.user?.email}>
                            {row?.original?.user?.email}
                        </p>
                    )}
                </div>
            </div>
        )
    },
    {
        id: 'createdAt',
        accessorKey: 'createdAt',
        enableColumnFilter: false,
        header: ({ column }) => (
            <Button
                variant="ghost"
                onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            >
                Processed At
                <ArrowUpDown size={ICON_SIZE_1} />
            </Button>
        ),
        cell: ({ row }) =>
            new Intl.DateTimeFormat('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false
            }).format(new Date(row.original.createdAt))
    },
    {
        accessorKey: 'transactions',
        header: 'Transaction Details',
        enableColumnFilter: false,
        cell: ({ row }) => {
            return (
                <TransactionDetailsModal data={row.original} />
            )
        }
    }
]


export default function TransactionList() {
    const dispatch = useAppDispatch()
    useDocumentTitle('Manage Transactions')
    const tableName = 'transactionHistoryList'
    const { pageIndex, pageSize, searchParams, sorting, refreshTrigger } = useAppSelector(getPaginationInfo(tableName))

    const { data: transactionList, loading } = useSelector((state: RootState) => state.transaction)

    const [searchInput, setSearchInput] = React.useState(searchParams?.search || '')
    const previousSearch = React.useRef<string | null>('')

    const debouncedSearch = React.useMemo(
        () =>
            debounceSearch((value: string) => {
                const trimmed = value.trim()

                if (trimmed === '' && value !== '') return
                table.setGlobalFilter(value)
                dispatch(setPageIndex({ table: tableName, pageIndex: 0 }))
                dispatch(setSearchParam({ table: tableName, key: 'search', value }))
                dispatch(triggerRefresh({ table: tableName, isRefresh: true }))
            }, 500),
        [dispatch]
    )

    const handleSorting = (value: unknown) => {
        const newSorting = typeof value === 'function' ? value(sorting) : value
        dispatch(setSorting({ table: tableName, sorting: newSorting }))
        dispatch(triggerRefresh({ table: tableName, isRefresh: true }))
    }

    const table = useReactTable({
        data: transactionList,
        columns,
        state: {
            sorting
        },
        getCoreRowModel: getCoreRowModel(),
        manualPagination: true,
        manualSorting: true,
        onSortingChange: handleSorting,
        manualFiltering: true
    })

    const fetchList = async () => {
        dispatch(setLoading(true))
        try {
            const sortingState = table.getState().sorting
            const orderColumn = sortingState.length > 0 ? sortingState[0].id : 'createdAt'
            const orderDirection = sortingState.length > 0 ? sortingState[0].desc ? 'desc' : 'asc' : 'desc'
            const response = await TransactionAPI.transactionHistoryList({
                start: pageIndex * pageSize,
                length: pageSize,
                search: searchParams?.search ?? '',
                order: orderDirection,
                orderBy: orderColumn,
                transaction_status: searchParams?.transaction_status
            })

            if (response.data.success === 1) {
                dispatch(setTransactionData(response.data.data.data || []))
                dispatch(setTotalRecords({ table: tableName, totalData: response.data.data.recordsTotal }))
                dispatch(setFilterRecords({ table: tableName, filterData: response.data.data.recordsFiltered }))
            } else {
                errorToast(response.data.message)
            }
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                errorToast(error.response?.data?.message || 'Network error')
            } else {
                errorToast('Error while fetching data')
            }
        } finally {
            dispatch(setLoading(false))
            dispatch(triggerRefresh({ table: tableName, isRefresh: false }))

        }
    }

    React.useEffect(() => {
        if (!transactionList.length) {
            fetchList()
        }
    }, [pageIndex,
        pageSize,
        searchParams.transaction_status,
        searchParams?.search,
        table.getState().sorting,
        dispatch])

    React.useEffect(() => {
        if (refreshTrigger) {
            fetchList()
        }
    }, [refreshTrigger])

    React.useEffect(() => {
        setSearchInput(searchParams?.search || '')
    }, [searchParams?.search])

    React.useEffect(() => {
        previousSearch.current = null
    }, [])

    const handleSearch = (value: string) => {
        setSearchInput(value)

        const trimmed = value.trim()
        if (trimmed === previousSearch.current) return

        previousSearch.current = trimmed
        debouncedSearch(trimmed)
    }

    return (
        <div className="flex w-full flex-col justify-start gap-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">

                    <RowPerPage tableName={tableName} />
                    <Dropdown
                        label="Status"
                        options={[
                            { label: 'All', value: 'all' },
                            { label: 'Success', value: 'success' },
                            { label: 'Failed', value: 'failed' }
                        ]}
                        value={searchParams?.transaction_status || 'all'}
                        onChange={(value) => {
                            dispatch(setPageIndex({ table: tableName, pageIndex: 0 }))
                            dispatch(setSearchParam({
                                table: tableName,
                                key: 'transaction_status',
                                value: value === 'all' ? '' : value
                            }))
                            dispatch(triggerRefresh({ table: tableName, isRefresh: true }))
                        }}
                    />
                </div>

                <div className="flex items-center gap-2">
                    <div className="flex gap-4 items-end">
                        <div className="relative">
                            <InputField
                                type='search'
                                placeholder={'Search'}
                                value={searchInput}
                                onChange={(event) => {
                                    handleSearch(event.target.value)
                                }}
                                className="max-w-sm"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="relative rounded-lg border">
                {loading && <div className="absolute inset-0 z-20 pointer-events-auto flex justify-center items-center top-[40px]">
                    <InlineLoader /></div>}
                <Table>
                    <TableHeader className="sticky top-0 z-10 bg-muted">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <TableHead key={header.id} colSpan={header.colSpan}>
                                        {header.isPlaceholder ? null : flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                    </TableHead>
                                ))}
                            </TableRow>
                        ))}
                    </TableHeader>

                    <TableBody>
                        {table.getRowModel().rows.length ? (
                            table.getRowModel().rows.map((row, key) => (
                                <TableRow key={key} data-state={row.getIsSelected() && 'selected'} className="relative z-0">
                                    {row.getVisibleCells().map((cell) => (
                                        <TableCell key={cell.id}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : !loading ? <TableRow>
                            <TableCell colSpan={columns.length} className="h-14 text-center font-bold">
                                No records found
                            </TableCell>
                        </TableRow> : <TableRow>
                            <TableCell colSpan={columns.length} className="h-14 text-center font-bold">
                            </TableCell>
                        </TableRow>
                        }
                    </TableBody>
                </Table>
            </div>
            {table.getRowModel().rows.length > 0 && (
                <div className="flex items-center justify-between">
                    <TablePagination
                        tableName={tableName}
                    />
                </div>
            )
            }

        </div>
    )
}
